<template>
  <div
    ref="rootEl"
    :class="[
      'relative w-full group',
      props.fillContainer ? 'h-full' : ''
    ]"
  >
    <div
      :class="[
        'relative mx-auto overflow-hidden cursor-pointer bg-black',
        props.fillContainer ? 'h-full w-full' : ''
      ]"
      :style="frameStyle"
      @click="emitOpenViewer"
      @mouseenter="isHovered = true"
      @mouseleave="isHovered = false"
    >
      <video
        ref="videoEl"
        :poster="poster || undefined"
        class="w-full h-full object-cover"
        playsinline
        preload="auto"
        muted
        @loadedmetadata="onLoadedMetadata"
        @loadeddata="onLoadedData"
        @timeupdate="onTimeUpdate"
        @ended="onEnded"
        @click="emitOpenViewer"
      >
        <source v-if="!fileId && src" :src="src" />
      </video>

      <div
        v-if="isLoading"
        class="absolute inset-0 flex items-center justify-center bg-black/35"
      >
        <div class="w-8 h-8 rounded-full border-2 border-white/50 border-t-transparent animate-spin"></div>
      </div>

      <!-- Hover controls (play/time/progress) -->
      <div
        v-if="isHovered && !isLoading"
        class="absolute inset-x-0 bottom-0 px-3 pb-2 pt-10 bg-gradient-to-t from-black/70 to-transparent"
        @click.stop
      >
        <input
          type="range"
          min="0"
          :max="Math.max(1, duration)"
          step="0.05"
          :value="currentTime"
          class="progress-slider w-full h-1.5 cursor-pointer"
          :style="progressTrackStyle"
          @input="onSeekInput"
        />

        <div class="mt-2 flex items-center justify-start gap-2">
          <button
            type="button"
            class="h-9 w-9 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80 transition"
            @click.stop="togglePlayPause"
          >
            <PlayIcon v-if="isPaused" class="w-5 h-5 ml-[1px]" />
            <PauseIcon v-else class="w-5 h-5" />
          </button>

          <p class="text-[13px] font-semibold text-white tabular-nums">
            {{ formattedCurrentTime }} / {{ formattedDuration }}
          </p>
        </div>
      </div>

      <!-- Always-visible sound control (lower right) -->
      <div class="absolute right-3 bottom-3 z-20" @click.stop>
        <div class="relative group/volume flex items-center justify-center">
          <button
            type="button"
            class="h-9 w-9 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80 transition"
            @click.stop="toggleMute"
          >
            <SpeakerXMarkIcon v-if="isMuted || volume === 0" class="w-5 h-5" />
            <SpeakerWaveIcon v-else class="w-5 h-5" />
          </button>

          <div
            class="pointer-events-auto absolute bottom-full mb-2 opacity-0 translate-y-1 group-hover/volume:opacity-100 group-hover/volume:translate-y-0 transition-all duration-150"
          >
            <div class="h-28 w-10 rounded-xl bg-black/70 border border-white/10 flex items-center justify-center">
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                :value="volume"
                class="volume-slider"
                :style="volumeTrackStyle"
                @input="onVolumeInput"
                @click.stop
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import Hls from 'hls.js';
import {
  PauseIcon,
  PlayIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
} from '@heroicons/vue/24/solid';

const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL;

const props = defineProps({
  fileId: { type: String, default: null },
  src: { type: String, default: null },
  playlistUrl: { type: String, default: null },
  featureInstanceId: { type: [String, Number], default: null },
  poster: { type: String, default: null },
  autoplay: { type: Boolean, default: true },
  muted: { type: Boolean, default: true },
  maxHeight: { type: Number, default: 680 },
  fillContainer: { type: Boolean, default: false },
});

const emit = defineEmits(['open-viewer', 'loaded']);

const rootEl = ref(null);
const videoEl = ref(null);
const isLoading = ref(true);
const isPaused = ref(true);
const isMuted = ref(!!props.muted);
const isHovered = ref(false);
const duration = ref(0);
const currentTime = ref(0);
const volume = ref(isMuted.value ? 0 : 1);
const lastNonZeroVolume = ref(1);
const isInViewport = ref(true);
const videoWidth = ref(16);
const videoHeight = ref(9);
const containerWidth = ref(0);

let hlsInstance = null;
let viewportObserver = null;
let resizeRafId = null;
let elementResizeObserver = null;
let resizeObserverRafId = null;
let attemptedDirectFallback = false;

const HLS_VOD_BUFFER_CONFIG = {
  maxBufferLength: 60,
  maxMaxBufferLength: 120,
  maxBufferSize: 90 * 1000 * 1000,
  backBufferLength: 45,
  manifestLoadingMaxRetry: 6,
  levelLoadingMaxRetry: 6,
  fragLoadingMaxRetry: 8,
  fragLoadingRetryDelay: 1000,
  fragLoadingMaxRetryTimeout: 10000,
};

const formattedDuration = computed(() => formatTime(duration.value));
const formattedCurrentTime = computed(() => formatTime(currentTime.value));
const progressPercent = computed(() => {
  const max = Math.max(1, Number(duration.value || 0));
  const cur = Math.max(0, Math.min(max, Number(currentTime.value || 0)));
  return `${(cur / max) * 100}%`;
});
const volumePercent = computed(() => {
  const v = Math.max(0, Math.min(1, Number(volume.value || 0)));
  return `${v * 100}%`;
});
const progressTrackStyle = computed(() => ({
  '--progress-percent': progressPercent.value,
}));
const volumeTrackStyle = computed(() => ({
  '--volume-percent': volumePercent.value,
}));
const mediaAspectRatio = computed(() => {
  const w = Number(videoWidth.value || 0);
  const h = Number(videoHeight.value || 0);
  if (w > 0 && h > 0) return w / h;
  return 16 / 9;
});
const frameStyle = computed(() => {
  if (props.fillContainer) {
    return {
      width: '100%',
      height: '100%',
      maxWidth: '100%',
      maxHeight: '100%',
    };
  }

  const ratio = mediaAspectRatio.value;
  const availableW = Math.max(1, Number(containerWidth.value || 0));
  const capH = Math.max(240, Number(props.maxHeight || 680));

  let targetW = availableW;
  let targetH = targetW / ratio;

  if (targetH > capH) {
    targetH = capH;
    targetW = targetH * ratio;
  }

  return {
    width: `${Math.floor(targetW)}px`,
    height: `${Math.floor(targetH)}px`,
    maxWidth: '100%',
    maxHeight: `${capH}px`,
  };
});

function buildPlaylistUrl() {
  const explicit = String(props.playlistUrl || '').trim();
  if (explicit) return explicit;

  if (!props.fileId || !apiProxyUrl) return null;

  const encodedFileId = encodeURIComponent(String(props.fileId));
  const featureId = String(props.featureInstanceId ?? '').trim();

  if (featureId) {
    return `${apiProxyUrl}/api/creator-newsfeed/stream-hls/playlist/${encodedFileId}?featureInstanceId=${encodeURIComponent(featureId)}`;
  }

  return `${apiProxyUrl}/api/stream-hls/playlist/${encodedFileId}`;
}

function destroyHls() {
  if (hlsInstance) {
    hlsInstance.destroy();
    hlsInstance = null;
  }
}

function emitOpenViewer() {
  emit('open-viewer');
}

function onLoadedMetadata() {
  const video = videoEl.value;
  duration.value = Number(video?.duration || 0);
  const w = Number(video?.videoWidth || 0);
  const h = Number(video?.videoHeight || 0);
  if (w > 0 && h > 0) {
    videoWidth.value = w;
    videoHeight.value = h;
  }
}

function onLoadedData() {
  isLoading.value = false;
  emit('loaded');
  maybeAutoplay();
}

function onTimeUpdate() {
  const video = videoEl.value;
  currentTime.value = Number(video?.currentTime || 0);
  isPaused.value = !!video?.paused;
}

function onEnded() {
  const video = videoEl.value;
  if (!video) return;
  if (!isInViewport.value) {
    isPaused.value = true;
    return;
  }
  video.currentTime = 0;
  video.play().catch(() => {});
  isPaused.value = !!video.paused;
}

function onSeekInput(event) {
  const video = videoEl.value;
  if (!video) return;
  const nextTime = Number(event?.target?.value || 0);
  video.currentTime = nextTime;
  currentTime.value = nextTime;
}

function togglePlayPause() {
  const video = videoEl.value;
  if (!video) return;

  if (video.paused) {
    video.play().catch(() => {});
  } else {
    video.pause();
  }

  isPaused.value = !!video.paused;
}

function onVolumeInput(event) {
  const video = videoEl.value;
  if (!video) return;
  const nextVolume = Math.min(1, Math.max(0, Number(event?.target?.value || 0)));
  volume.value = nextVolume;
  if (nextVolume > 0) lastNonZeroVolume.value = nextVolume;
  video.volume = nextVolume;
  video.muted = nextVolume === 0;
  isMuted.value = !!video.muted;
}

function toggleMute() {
  const video = videoEl.value;
  if (!video) return;

  if (video.muted || volume.value === 0) {
    const restoreVolume = Math.min(1, Math.max(0.05, Number(lastNonZeroVolume.value || 1)));
    video.volume = restoreVolume;
    volume.value = restoreVolume;
    video.muted = false;
  } else {
    if (volume.value > 0) lastNonZeroVolume.value = volume.value;
    volume.value = 0;
    video.volume = 0;
    video.muted = true;
  }

  isMuted.value = !!video.muted;
}

function maybeAutoplay() {
  const video = videoEl.value;
  if (!video) return;

  video.muted = isMuted.value;
  video.volume = volume.value;

  if (!props.autoplay || !isInViewport.value) return;
  video.play().catch(() => {});
  isPaused.value = !!video.paused;
}

function initViewportObserver() {
  if (!rootEl.value || typeof IntersectionObserver === 'undefined') return;

  viewportObserver = new IntersectionObserver(
    (entries) => {
      const entry = entries[0];
      if (!entry) return;
      isInViewport.value = entry.isIntersecting && entry.intersectionRatio > 0.35;

      const video = videoEl.value;
      if (!video) return;

      if (isInViewport.value) {
        if (props.autoplay && video.paused) {
          video.play().catch(() => {});
          isPaused.value = !!video.paused;
        }
      } else if (!video.paused) {
        video.pause();
        isPaused.value = true;
      }
    },
    { threshold: [0, 0.35, 0.75, 1] }
  );

  viewportObserver.observe(rootEl.value);
}

function initResizeObserver() {
  const el = rootEl.value;
  if (!el) return;
  const nextWidth = Math.round(Number(el.clientWidth || 0));
  if (Math.abs(nextWidth - containerWidth.value) >= 1) {
    containerWidth.value = nextWidth;
  }
}

function destroyResizeObserver() {
  if (resizeRafId != null) {
    cancelAnimationFrame(resizeRafId);
    resizeRafId = null;
  }

  if (resizeObserverRafId != null) {
    cancelAnimationFrame(resizeObserverRafId);
    resizeObserverRafId = null;
  }

  if (elementResizeObserver) {
    elementResizeObserver.disconnect();
    elementResizeObserver = null;
  }
}

function onWindowResize() {
  if (resizeRafId != null) return;
  resizeRafId = requestAnimationFrame(() => {
    resizeRafId = null;
    initResizeObserver();
  });
}

function destroyViewportObserver() {
  if (viewportObserver) {
    viewportObserver.disconnect();
    viewportObserver = null;
  }
}

function initSource() {
  const video = videoEl.value;
  if (!video) return;

  isLoading.value = true;
  attemptedDirectFallback = false;
  currentTime.value = 0;
  duration.value = 0;
  destroyHls();

  video.muted = isMuted.value;
  video.volume = volume.value;
  video.loop = false;

  const playlistUrl = buildPlaylistUrl();
  if (playlistUrl) {
    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = playlistUrl;
      video.load();
      return;
    }

    if (Hls.isSupported()) {
      const hls = new Hls({
        debug: false,
        ...HLS_VOD_BUFFER_CONFIG,
        lowLatencyMode: false,
        capLevelToPlayerSize: true,
        startFragPrefetch: true,
        xhrSetup(xhr) {
          xhr.withCredentials = true;
        },
      });

      hlsInstance = hls;
      hls.startLevel = 0;
      hls.loadSource(playlistUrl);
      hls.attachMedia(video);

      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (!data?.fatal) {
          if (data?.details === Hls.ErrorDetails.BUFFER_STALLED_ERROR) {
            hls.startLoad();
          }
          return;
        }
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          if (!attemptedDirectFallback && props.src) {
            attemptedDirectFallback = true;
            destroyHls();
            video.src = props.src;
            video.load();
            return;
          }
          hls.startLoad();
          return;
        }
        if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          if (!attemptedDirectFallback && props.src) {
            attemptedDirectFallback = true;
            destroyHls();
            video.src = props.src;
            video.load();
            return;
          }
          hls.recoverMediaError();
          return;
        }
        destroyHls();
        if (!attemptedDirectFallback && props.src) {
          attemptedDirectFallback = true;
          video.src = props.src;
          video.load();
          return;
        }
        isLoading.value = false;
      });
      return;
    }
  }

  if (props.src) {
    video.src = props.src;
    video.load();
  } else {
    isLoading.value = false;
  }
}

function formatTime(rawSeconds) {
  const total = Math.max(0, Math.floor(Number(rawSeconds || 0)));
  const m = Math.floor(total / 60);
  const s = String(total % 60).padStart(2, '0');
  return `${m}:${s}`;
}

watch(
  () => [props.fileId, props.src, props.playlistUrl, props.featureInstanceId],
  () => {
    initSource();
  }
);

onMounted(() => {
  initSource();
  initViewportObserver();
  initResizeObserver();
  if (typeof ResizeObserver !== 'undefined' && rootEl.value) {
    elementResizeObserver = new ResizeObserver(() => {
      if (resizeObserverRafId != null) return;
      resizeObserverRafId = requestAnimationFrame(() => {
        resizeObserverRafId = null;
        initResizeObserver();
      });
    });
    elementResizeObserver.observe(rootEl.value);
  }
  window.addEventListener('resize', onWindowResize, { passive: true });
});

onBeforeUnmount(() => {
  destroyHls();
  destroyViewportObserver();
  destroyResizeObserver();
  window.removeEventListener('resize', onWindowResize);
});
</script>

<style scoped>
.volume-slider {
  -webkit-appearance: none;
  appearance: none;
  width: 76px;
  height: 6px;
  transform: rotate(-90deg);
  transform-origin: center;
  background: transparent;
  border: none;
  outline: none;
  cursor: pointer;
  accent-color: #2d8cff;
}

.progress-slider {
  -webkit-appearance: none;
  appearance: none;
  background: transparent;
  border: none;
  outline: none;
}

.progress-slider:focus,
.volume-slider:focus {
  outline: none;
}

.progress-slider::-webkit-slider-runnable-track {
  height: 6px;
  border-radius: 9999px;
  background: linear-gradient(
    to right,
    rgba(45, 140, 255, 0.95) 0%,
    rgba(45, 140, 255, 0.95) var(--progress-percent),
    rgba(212, 212, 216, 0.55) var(--progress-percent),
    rgba(212, 212, 216, 0.55) 100%
  );
  border: none;
}

.progress-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 14px;
  height: 14px;
  border-radius: 9999px;
  border: none;
  background: #2d8cff;
  margin-top: -4px;
}

.progress-slider::-moz-range-track {
  height: 6px;
  border-radius: 9999px;
  background: rgba(212, 212, 216, 0.55);
  border: none;
}

.progress-slider::-moz-range-progress {
  height: 6px;
  border-radius: 9999px;
  background: rgba(45, 140, 255, 0.95);
}

.progress-slider::-moz-range-thumb {
  width: 14px;
  height: 14px;
  border-radius: 9999px;
  border: none;
  background: #ffffff;
}

.volume-slider::-webkit-slider-runnable-track {
  height: 6px;
  border-radius: 9999px;
  background: linear-gradient(
    to right,
    rgba(45, 140, 255, 0.95) 0%,
    rgba(45, 140, 255, 0.95) var(--volume-percent),
    rgba(212, 212, 216, 0.55) var(--volume-percent),
    rgba(212, 212, 216, 0.55) 100%
  );
  border: none;
}

.volume-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 14px;
  height: 14px;
  border-radius: 9999px;
  border: none;
  background: #2d8cff;
  margin-top: -4px;
}

.volume-slider::-moz-range-track {
  height: 6px;
  border-radius: 9999px;
  background: rgba(212, 212, 216, 0.55);
  border: none;
}

.volume-slider::-moz-range-progress {
  height: 6px;
  border-radius: 9999px;
  background: rgba(45, 140, 255, 0.95);
  border: none;
}

.volume-slider::-moz-range-thumb {
  width: 14px;
  height: 14px;
  border-radius: 9999px;
  border: none;
  background: #2d8cff;
}
</style>
